import { makeEnum, makeEnumWithData, makeOptions,
  makeEnumWithDataAndLabels,
  setupClass, lookupData, Matches,
  interpolateInMap, doubleInterpolateInMap,
  IdsMap, PleaseContactStr,
  IntervalTimer,
} from '../Base.js'
import {
  secsToDurationStr,
  generateItemName,
} from '../SharedUtils.js'
import * as ser from '../Common/SerUtil.js'
import { ComponentArray } from '../Common/ComponentArray.js'
import { OutputValue } from '../Common/OutputValue.js'
import { InputComponent } from '../Common/InputComponent.js'
import { TypesSet } from '../Components/TypesSet.js'
import { MatrixPrototype } from '../Common/Math.js'
import { MonthHourMap } from './MonthHourMap.js'

export { Units } from '../Common/Units.js'

import { RecoveryType, } from '../Components/HouseVentilationInfiltration.js'
import { makeNoYesField, makeYesNoField, YesNo, } from '../Components/Common.js'
import { Field, FieldGroup, FieldType, } from '../Common/Field.js'

import { Zone } from './Zone.js'
import { Schedule } from './Schedule.js'

import { CalcContext } from '../Common/CalcContext.js'
import { ProjectErrors } from '../ProjectErrors.js'

import { SystemLoadCalculator } from './SystemLoadCalculator.js'
import { SystemDesignTempInputs } from './SystemDesignTempInputs.js'
import { SystemHeatingDesignTemps, SystemCoolingDesignTemps } from './SystemDesignTemps.js'

import { gApp, DebugOn, IsTestEnv, } from '../Globals.js'

export class SystemFan {
  init() {
    this.motorIsInAirStream = makeYesNoField('Motor is in air stream')
    this.externalStaticPressure = new Field({
      name: 'External Static Pressure',
      type: FieldType.SmallPressure,
      defaultValue: 1,
      metricDefaultValue: 250,
      allowMin: false,
    })
    this.fanEfficiency = new Field({
      name: 'Fan Efficiency',
      type: FieldType.Percent,
      defaultValue: 75,
      allowMin: false,
    })
    this.motorEfficiency = new Field({
      name: 'Motor Efficiency',
      type: FieldType.Percent,
      defaultValue: 75,
      allowMin: false,
    })

    this.fields = [
      'motorIsInAirStream',
      'externalStaticPressure',
      'fanEfficiency',
      'motorEfficiency',
    ]

    this.serFields = [
      ...this.fields,
    ]
    this.childObjs = '$auto'
    this.objInfo = {
      _name: 'SystemFan',
      path: 'system-fan',
    }
  }
}
setupClass(SystemFan)

class SystemFans extends InputComponent {
  init() {
    this.useSupplyFan = makeYesNoField('Use supply fan', {bold: true})
    this.supplyFan = SystemFan.create()
    this.updater.setEnabledWhen('supply-fan', this.supplyFan, () => {
      return this.useSupplyFan.value == YesNo.Yes;
    });

    this.useReturnFan = makeYesNoField('Use return fan', {
      bold: true,
      defaultValue: YesNo.No,
    })
    this.returnFan = SystemFan.create()
    this.updater.setEnabledWhen('return-fan', this.returnFan, () => {
      return this.useReturnFan.value == YesNo.Yes;
    });

    this.serFields = [
      'useSupplyFan',
      'supplyFan',
      'useReturnFan',
      'returnFan',
    ]
    this.childObjs = '$auto'
    this.objInfo = {
      _name: 'SystemFans',
      path: 'fans',
    }
  }

  usingSupplyFan() {
    return this.useSupplyFan.value == YesNo.Yes;
  }

  usingReturnFan() {
    return this.useReturnFan.value == YesNo.Yes;
  }
}
setupClass(SystemFans)

let ExhaustAirType = makeEnum({
  MatchSupplyFlowThroughUnit: `Match supply flow through unit`,
  SpecifiedPortionOfSupplyFlow: `Specified portion of supply flow`,
  ManuallySetExhaustFlow: `Manually set exhaust flow`,
})

export class SystemHeatRecovery {
  init() {
    this.recoveryType = Field.makeSelect('Recovery Type', RecoveryType, {bold: true})

    this.exhaustAir = FieldGroup.fromDict({
      'entryType': Field.makeSelect('Exhaust Air', ExhaustAirType),
      'portionOfSupplyFlow': new Field({
        name: 'Portion of Supply Flow',
        type: FieldType.Percent,
      }),
      'manualValue': new Field({
        name: 'Flow',
        type: FieldType.AirFlow,
        defaultValue: 100,
        min: 0,
      }),
    })
    this.exhaustAir.get('entryType').setVisibility(() => {
      return this.recoveryType.value != RecoveryType.None;
    })
    this.exhaustAir.get('portionOfSupplyFlow').setVisibility(() => {
      return this.recoveryType.value != RecoveryType.None && this.exhaustAir.get('entryType').value == ExhaustAirType.SpecifiedPortionOfSupplyFlow;
    });
    this.exhaustAir.get('manualValue').setVisibility(() => {
      return this.recoveryType.value != RecoveryType.None && this.exhaustAir.get('entryType').value == ExhaustAirType.ManuallySetExhaustFlow;
    });

    this.hrvGroup = new FieldGroup([
      new Field({
        key: 'summerEfficiency',
        name: 'Summer Efficiency',
        type: FieldType.Percent,
        defaultValue: 55,
      }),
      new Field({
        key: 'winterEfficiency',
        name: 'Winter Efficiency',
        type: FieldType.Percent,
        defaultValue: 75,
      })
    ]);
    this.hrvGroup.setVisibility(() => {
      return this.recoveryType.value == RecoveryType.HRV;
    })

    this.ervGroup = new FieldGroup([
      new Field({
        key: 'summerSensibleEfficiency',
        name: 'Summer sensible efficiency',
        type: FieldType.Percent,
        defaultValue: 55,
      }),
      new Field({
        key: 'summerTotalEfficiency',
        name: 'Summer total efficiency',
        type: FieldType.Percent,
        defaultValue: 60,
      }),
      new Field({
        key: 'winterSensibleEfficiency',
        name: 'Winter sensible efficiency',
        type: FieldType.Percent,
        defaultValue: 75,
      }),
      new Field({
        key: 'winterTotalEfficiency',
        name: 'Winter total efficiency',
        type: FieldType.Percent,
        defaultValue: 80,
      }),
    ]);
    this.ervGroup.setVisibility(() => {
      return this.recoveryType.value == RecoveryType.ERV;
    })

    this.serFields = [
      'recoveryType',
      'exhaustAir',
      'hrvGroup',
      'ervGroup',
    ]
    this.childObjs = '$auto'
    this.objInfo = {
      _name: 'HeatRecovery',
      path: 'ventilation/heat-recovery',
    }
  }

  calc_Q_exhaust(ctx, V_ot) {
    let entryType = this.exhaustAir.get('entryType').value;
    ctx.exhaustEntryType = entryType;
    if (entryType == ExhaustAirType.MatchSupplyFlowThroughUnit) {
      return V_ot;
    } else if (entryType == ExhaustAirType.SpecifiedPortionOfSupplyFlow) {
      return V_ot * this.exhaustAir.get('portionOfSupplyFlow').value / 100.0;
    } else if (entryType == ExhaustAirType.ManuallySetExhaustFlow) {
      return this.exhaustAir.get('manualValue').value;
    } else {
      throw new Error(`Unknown entry type: ${entryType}`);
    }
  }
}
setupClass(SystemHeatRecovery)

// Right now, we only support CAV and VAV systems
export let SystemType = makeEnum({
  CAV: 'CAV',
  VAV: 'VAV',
  // Unsupported
  RadiantInductionOrChilledBeam: 'Radiant, induction, or chilled beam',
  // Unsupported
  Unknown: 'Unknown',
})

let ReliefAirOptions = makeEnum({
  PercentageOfVentilationAir: 'Percentage of ventilation air',
  Manual: 'Manual',
})

export let ZoneAirflowCalcMethod = makeEnum({
  SumOfPeaks: 'Sum of peaks',
  TotalPeak: 'Total peak',
})

function checkHeatingSupplyTempSupportsZones(field, system) {
  if (system.zones.length == 0) {
    return;
  }
  let heatingSupplyTemp = field.value;
  let maxWinterTemp = null;
  let unitsLabel = system.zones[0].winterIndoorTemp.getDisplayUnitsLabel();
  for (const zone of system.zones) {
    let zoneTargetTemp = zone.winterIndoorTemp.value;
    maxWinterTemp = maxWinterTemp == null ? zoneTargetTemp : Math.max(maxWinterTemp, zoneTargetTemp);
  }
  if (heatingSupplyTemp <= maxWinterTemp) {
    field.setPriorityErrorMsg(`This temperature must be >${maxWinterTemp}${unitsLabel} in order to heat` +
      ` all zones to their desired winter indoor temperatures.`);
    return;
  }
  field.clearPriorityErrorMsg()
}

function checkCoolingSupplyTempSupportsZones(field, system) {
  if (system.zones.length == 0) {
    return;
  }
  let coolingSupplyTemp = field.value;
  let minSummerTemp = null;
  let unitsLabel = system.zones[0].summerIndoorTemp.getDisplayUnitsLabel();
  for (const zone of system.zones) {
    let zoneTargetTemp = zone.summerIndoorTemp.value;
    minSummerTemp = minSummerTemp == null ? zoneTargetTemp : Math.min(minSummerTemp, zoneTargetTemp);
  }
  if (coolingSupplyTemp >= minSummerTemp) {
    field.setPriorityErrorMsg(`This temperature must be <${minSummerTemp}${unitsLabel} in order to cool` +
      ` all zones to their desired summer indoor temperatures.`);
    return;
  }
  field.clearPriorityErrorMsg()
}

class CAVSystemInputs {
  init(system) {
    this.system = system;

    this.heatingSupplyTemp = new Field({
      name: 'Heating Supply Temp',
      type: FieldType.Temp,
      defaultValue: 90,
      metricDefaultValue: 32,
    })
    this.heatingSupplyTemp.makeUpdater((field) => {
      checkHeatingSupplyTempSupportsZones(field, this.system);
    })
    this.coolingSupplyTemp = new Field({
      name: 'Cooling Supply Temp',
      type: FieldType.Temp,
      defaultValue: 55,
      metricDefaultValue: 13,
    })
    this.coolingSupplyTemp.makeUpdater((field) => {
      checkCoolingSupplyTempSupportsZones(field, this.system);
    })

    this.includeZoneTerminalReheat = makeYesNoField('Include zone terminal reheat')
    // NOTE - field is called zoneAirflowCalcMethod for historical reasons, but really
    // this is for airflow of the system.
    this.zoneAirflowCalcMethod = Field.makeSelect('System Airflow Calculation Method', ZoneAirflowCalcMethod)

    this.fields = [
      'heatingSupplyTemp',
      'coolingSupplyTemp',
      'includeZoneTerminalReheat',
      'zoneAirflowCalcMethod',
    ]

    this.serFields = [
      ...this.fields
    ]
    this.childObjs = '$auto'
    this.objInfo = {
      _name: "CAV Details",
      path: 'general/cav',
    }
  }
}
setupClass(CAVSystemInputs)

class VAVSystemInputs {
  init(system) {
    this.system = system;

    this.heatingSupplyTemp = new Field({
      name: 'Heating Supply Temp',
      type: FieldType.Temp,
      defaultValue: 90,
      metricDefaultValue: 32,
    })
    this.heatingSupplyTemp.makeUpdater((field) => {
      checkHeatingSupplyTempSupportsZones(field, this.system);
    })
    this.coolingSupplyTemp = new Field({
      name: 'Cooling Supply Temp',
      type: FieldType.Temp,
      defaultValue: 55,
      metricDefaultValue: 13,
    })
    this.coolingSupplyTemp.makeUpdater((field) => {
      checkCoolingSupplyTempSupportsZones(field, this.system);
    })
    this.includeZoneTerminalReheat = makeYesNoField('Include zone terminal reheat')

    this.fields = [
      'heatingSupplyTemp',
      'coolingSupplyTemp',
      'includeZoneTerminalReheat',
    ]

    this.serFields = [
      ...this.fields
    ]
    this.childObjs = '$auto'
    this.objInfo = {
      _name: "VAV Details",
      path: 'general/vav',
    }
  }
}
setupClass(VAVSystemInputs)

let PrimaryAirCoolingEntry = makeEnum({
  Airflow: 'Airflow',
  WetBulbTemp: 'Supply Wet Bulb Temp',
  DewPointTemp: 'Supply Dew Point Temp',
  RelativeHumidity: 'Supply Relative Humidity',
})

/*
TODO - currently unused
*/
class RadiantInductionSystemInputs {
  init() {
    this.primaryAirHeatingSupplyTemp = new Field({
      name: 'Primary Air Heating Supply Temp',
      type: FieldType.Temp,
    })
    this.primaryAirCoolingSupplyTemp = new Field({
      name: 'Primary Air Cooling Supply Temp',
      type: FieldType.Temp,
    })
    this.primaryAirCooling = Field.makeSelect('Primary Air Cooling', PrimaryAirCoolingEntry)

    this.airflow = new Field({
      name: 'Airflow',
      type: FieldType.Flow,
    })
    this.airflow.setVisibility(() => {
      return this.primaryAirCooling.value == PrimaryAirCoolingEntry.Airflow;
    })
    this.supplyWetBulbTemp = new Field({
      name: 'Supply Wet Bulb Temp',
      type: FieldType.Temp,
    })
    this.supplyWetBulbTemp.setVisibility(() => {
      return this.primaryAirCooling.value == PrimaryAirCoolingEntry.WetBulbTemp;
    })
    this.supplyDewPointTemp = new Field({
      name: 'Supply Dew Point Temp',
      type: FieldType.Temp,
    })
    this.supplyDewPointTemp.setVisibility(() => {
      return this.primaryAirCooling.value == PrimaryAirCoolingEntry.DewPointTemp;
    })
    this.supplyRelativeHumidity = new Field({
      name: 'Supply Relative Humidity',
      type: FieldType.Percent,
    })
    this.supplyRelativeHumidity.setVisibility(() => {
      return this.primaryAirCooling.value == PrimaryAirCoolingEntry.RelativeHumidity;
    })

    this.fields = [
      'primaryAirHeatingSupplyTemp',
      'primaryAirCoolingSupplyTemp',
      'primaryAirCooling',
      'airflow',
      'supplyWetBulbTemp',
      'supplyDewPointTemp',
      'supplyRelativeHumidity',
    ]

    this.serFields = [
      ...this.fields
    ]
    this.childObjs = '$auto'
    this.objInfo = {
      _name: "Radiant Induction System Details",
      path: 'radiant-induction-details',
    }
  }
}
setupClass(RadiantInductionSystemInputs)

export let SystemResultsState = makeEnum({
  None: 'None',
  Calculating: 'Calculating',
  Ready: 'Ready',
})

export let SystemResultsOutcome = makeEnum({
  FailedWithProjectErrors: 'FailedWithProjectErrors',
  FailedWithCalculationError: 'FailedWithCalculationError',
  Success: 'Success',
})

export class SystemLoadResults {
  init(calcContext, basicInfo) {
    this.calcContext = calcContext
    this.projectName = basicInfo?.projectName || '';
    this.systemName = basicInfo?.systemName || '';

    this.heatingDesignTemp = null
    this.coolingDesignTemp = null

    this.results = null
    this.projectErrors = null
    this.calculationError = null
    this.startTime = IsTestEnv() ? 10000 : Date.now();
    this.endTime = null;

    // Note - we don't save the calcContext, but we want to be able to serialize
    // this for internal debugging and PDF generation.
    this.serFields = [
      'projectName',
      'systemName',
      'heatingDesignTemp',
      'coolingDesignTemp',
      ser.genericObjectField('results', [
        OutputValue,
        MatrixPrototype,
        MonthHourMap,
      ]),
      // Do not serialize project errors
      //'projectErrors',
      'calculationError',
      'startTime',
      'endTime',
    ]
  }

  getProjectName() {
    return this.projectName;
  }

  getSystemName() {
    return this.systemName;
  }

  getOutcome() {
    if (this.projectErrors != null) {
      return SystemResultsOutcome.FailedWithProjectErrors;
    } else if (this.calculationError != null) {
      return SystemResultsOutcome.FailedWithCalculationError;
    }
    return SystemResultsOutcome.Success;
  }

  setDesignTemps(heatingDesignTemp, coolingDesignTemp) {
    this.heatingDesignTemp = heatingDesignTemp;
    this.coolingDesignTemp = coolingDesignTemp;
  }

  setProjectErrors(errors) {
    this.projectErrors = errors;
  }

  getProjectErrors() {
    return this.projectErrors;
  }

  getCalculationError() {
    return this.calculationError;
  }

  setCalculationError(err) {
    this.calculationError = err.toString();
    this.endTime = IsTestEnv() ? 20000 : Date.now();
  }

  setResults(results) {
    this.results = results;
    this.endTime = IsTestEnv() ? 20000 : Date.now();
  }

  isSuccess() {
    return this.getOutcome() == SystemResultsOutcome.Success;
  }
  
  getOutputSummary() {
    return this.results.outputSummary;
  }

  getZoneLoadsSummary() {
    return this.results.zoneLoadsSummary;
  }

  getAnnualLoadsSummary() {
    return this.results.annualLoadsSummary;
  }

  getLoadCharts() {
    return this.results.loadCharts;
  }

  getSystemInputSummary() {
    return this.results.systemInputSummary;
  }

  getSpaceInputSummary() {
    return this.results.spaceInputSummary;
  }

  getSpaceResultsNodes() {
    return this.results.spaceResultsNodes;
  }

  getSpaceResultsData() {
    return this.results.spaceResultsData;
  }

  getZoneResultsData() {
    return this.results.zoneResultsData;
  }

  getSpaceBreakdownTables() {
    return this.results.spaceBreakdownTables;
  }

  getProgressStr() {
    if (!this.calcContext) {
      return 'Calculation in progress...';
    }
    return this.calcContext.getProgressText();
  }

  getDetailedProgressStr() {
    if (!this.calcContext) {
      return 'Calculation in progress...';
    }
    return this.calcContext.getDetailedProgressText();
  }

  getProgressPercent() {
    if (!this.calcContext) {
      return 0;
    }
    return this.calcContext.getProgressPercent();
  }

  getTimeCalculatingStr() {
    let secsCalculating = (Date.now() - this.startTime) / 1000;
    return secsToDurationStr(secsCalculating);
  }

  getDurationStr() {
    let secsCalculating = (this.endTime - this.startTime) / 1000;
    return secsToDurationStr(secsCalculating);
  }

  getStartDateStr() {
    return new Date(this.startTime).toLocaleString();
  }

  getHeatingDesignTempStr() {
    return SystemHeatingDesignTemps.getLabel(this.heatingDesignTemp);
  }

  getCoolingDesignTempStr() {
    return SystemCoolingDesignTemps.getLabel(this.coolingDesignTemp);
  }
}
setupClass(SystemLoadResults)

export let VentilationInputMode = makeEnum({
  Manual: 'Enter manually',
  Ashrae62p1: 'Calculate using ASHRAE 62.1',
})

class SystemSafetyFactors {
  init() {
    this.sensibleGains = new Field({
      name: 'Sensible Gains',
      type: FieldType.Percent,
      defaultValue: 0,
      allowMin: true,
      max: null,
      basePath: 'general',
    })
    this.latentGains = new Field({
      name: 'Latent Gains',
      type: FieldType.Percent,
      defaultValue: 0,
      allowMin: true,
      max: null,
      basePath: 'general',
    })
    this.sensibleLosses = new Field({
      name: 'Sensible Losses',
      type: FieldType.Percent,
      defaultValue: 0,
      allowMin: true,
      max: null,
      basePath: 'general',
    })

    this.fields = [
      'sensibleGains',
      'latentGains',
      'sensibleLosses',
    ]
    this.serFields = [
      ...this.fields,
    ]
    this.childObjs = '$auto'
    this.objInfo = {
      _name: 'Safety Factors',
      path: 'general/safety-factors',
    }
  }
}
setupClass(SystemSafetyFactors)

export class System extends InputComponent {
  init(name, makeId) {
    this.name = Field.makeName(`System Name`, name)
    this.id = makeId ? gApp.proj().makeId('System') : 0;
    this.zones = new ComponentArray(Zone)

    this.estimatedTotalOccupancy = new Field({
      name: 'Estimated Total Occupancy',
      type: FieldType.Count,
      allowMin: true,
      defaultValue: 0,
      basePath: 'general',
    })

    this.systemVentilationInputMode = Field.makeSelect("Input mode", VentilationInputMode,
      {bold: true});
    this.systemVentilationIntake = new Field({
      name: 'System Ventilation Intake (V<sub>ot</sub>)',
      type: FieldType.AirFlow,
      allowMin: true,
      defaultValue: 0,
      basePath: 'ventilation',
      requiresInput: true,
    })
    this.systemVentilationIntake.setVisibility(() => {
      return this.systemVentilationInputMode.value == VentilationInputMode.Manual;
    });

    this.demandControlledVentilation = FieldGroup.fromDict({
      'use': makeNoYesField('Use demand controlled ventilation', {bold: true}),
      'matchOccupancySchedule': makeYesNoField('Match system occupancy schedule'),
      'schedule': Field.makeTypeSelect('Schedule', gApp.proj().schedules, null, {
        errorWhenEmpty: `You must create a Schedule`,
        metadata: {
          typeClass: Schedule,
        }
      }),
      'dcvMinVentilation': new Field({
        name: 'Minimum Ventilation for DCV (V<sub>ot,min</sub>)',
        type: FieldType.AirFlow,
        allowMin: true,
        defaultValue: 0,
        requiresInput: true,
      }),
    }, {
      basePath: 'ventilation',
    })
    this.demandControlledVentilation.get('matchOccupancySchedule').setVisibility(() => {
      return this.demandControlledVentilation.get('use').value == YesNo.Yes;
    })
    this.demandControlledVentilation.get('schedule').setVisibility(() => {
      return this.demandControlledVentilation.get('use').value == YesNo.Yes &&
        this.demandControlledVentilation.get('matchOccupancySchedule').value == YesNo.No;
    });
    this.demandControlledVentilation.get('dcvMinVentilation').setVisibility(() => {
      return this.usingDemandControlledVentilation();
    })

    this.heatRecovery = SystemHeatRecovery.create()

    this.economizer = FieldGroup.fromDict({
      'use': makeNoYesField('Uses economizer', {bold: true}),
    }, {basePath: 'ventilation'})

    this.addWinterHumidification = makeNoYesField('Add winter humidification', {
      bold: true,
      basePath: 'ventilation',
    })
    this.winterHumiditySetpoint = new Field({
      name: 'Winter Humidity Setpoint',
      type: FieldType.Percent,
      requiresInput: true,
      allowMin: false,
      basePath: 'ventilation',
    })
    this.winterHumiditySetpoint.setVisibility(() => {
      return this.addWinterHumidification.value == YesNo.Yes;
    })
    this.winterHumidityFields = [
      'addWinterHumidification',
      'winterHumiditySetpoint',
    ]

    this.systemFans = SystemFans.create()

    // Note: we only support CAV and VAV for now
    this.systemType = new Field({
      name: 'System Type',
      type: FieldType.Select,
      choices: makeOptions(SystemType, [SystemType.CAV, SystemType.VAV]),
      bold: true,
      basePath: 'general',
    })
    // NOTE - this used to be visible, but we default to always "YES" for now.
    // Do not delete for now, in case we end up moving to an "Advanced" section later.
    this.separateAirflow = makeYesNoField('Separate airflow for heating and cooling?', {
      basePath: 'general',
    })
    this.detailFields = [
      'systemType',
      //'separateAirflow',
    ]
    this.safetyFactors = SystemSafetyFactors.create()

    this.cavSystemInputs = CAVSystemInputs.create(this)
    this.updater.setEnabledWhen('cav-inputs', this.cavSystemInputs, () => {
      return this.systemType.value == SystemType.CAV;
    });

    this.vavSystemInputs = VAVSystemInputs.create(this)
    this.updater.setEnabledWhen('vav-inputs', this.vavSystemInputs, () => {
      return this.systemType.value == SystemType.VAV;
    });

    this.designTempInputs = SystemDesignTempInputs.create()

    this.resultsState = SystemResultsState.None;
    this.loadResults = SystemLoadResults.create(null, null);
    this.debugCalcOptions = {
      recordCalculationLog: true,
      calcFirstHourOnly: false,
      useDummySpaceLoads: false,
    }

    this.serFields = [
        'name',
        'id',
        ser.arrayField('zones', () => { return Zone.create(); }),
        'estimatedTotalOccupancy',
        'systemVentilationInputMode',
        'systemVentilationIntake',
        'demandControlledVentilation',
        'heatRecovery',
        'economizer',
        ...this.winterHumidityFields,
        ...this.detailFields,
        'systemFans',
        'safetyFactors',
        'cavSystemInputs',
        'vavSystemInputs',
        'designTempInputs',

        // Note: We do not save the load results for now. Recalculate when you open the project.
        //'resultsState',
        //'loadResults',
    ]
    this.childObjs = '$auto'
    this.objInfo = {
      _uniqueName: true,
      _name: () => {
        return this.name.value;
      },
      uniquePath: true,
      path: () => {
        return `systems/${this.id}`;
      },
    }
  }

  setupDefaults() {
    // Add a default zone
    if (this.zones.length === 0) {
      this.createZone();
    }
  }

  getObjErrors() {
    let errors = [];
    this.updater.addErrors(errors);
    if (this.zones.length === 0) {
      errors.push({
        msg: 'You must add at least one Zone to the System.',
        showInComponentErrorsList: false,
        path: 'zone/at-least-one',
      });
    }
    return errors;
  }

  postReadFromJson(jsonData, opts) {
    // If we read that the load results were calculating, we have to set to None, since we can't
    // resume them here.
    console.log("On postReadFromJson");
    if (this.resultsState == SystemResultsState.Calculating) {
      console.log("Resetting results state to None (was Calculating on save)");
      this.resultsState = SystemResultsState.None;
      this.loadResults = SystemLoadResults.create(null, null);
    }
  }

  getName() {
    return this.name.value;
  }

  static getTableInfo() {
    return {
      typeName: 'System',
      pagePath: 'systems',
      allowDuplicate: true,
      excludeFieldsForDuplicate: ['resultsState', 'loadResults'],
      createFunc: (name, makeId) => {
        let system = System.create(name, makeId);
        system.setupDefaults();
        return system;
      },
      columns: {
        'name': {
          label: 'Name',
        }
      }
    }
  }

  getTableData() {
    return {
      name: this.name.value,
    }
  }

  getInputPage() {
    return {
      label: `${this.getName()}`,
      path: `systems/${this.id}`,
      openEditor: () => {
        gApp.proj().editorState.selectedSystem = this;
        gApp.router.push({name: 'systems'});
        // Scroll to the top of the page
        window.scrollTo(0, 0, {
          behavior: 'smooth'
        });
      },
      typeName: 'System',
      allowFixingInPopup: false,
    }
  }

  getResultsState() {
    return this.resultsState;
  }

  getResultsStatusStr() {
    if (this.resultsState == SystemResultsState.None) {
      return 'No results yet';
    } else if (this.resultsState == SystemResultsState.Calculating) {
      return 'Calculating...';
    } else if (this.resultsState == SystemResultsState.Ready) {
      let outcome = this.loadResults.getOutcome();
      if (outcome == SystemResultsOutcome.FailedWithProjectErrors ||
          outcome == SystemResultsOutcome.FailedWithCalculationError) {
        return 'Error calculating loads';
      } else if (outcome == SystemResultsOutcome.Success) {
        return 'Results ready!';
      } else {
        throw new Error(`Unknown results outcome: ${outcome}`);
      }
    } else {
      throw new Error(`Unknown results state: ${this.resultsState}`);
    }
  }

  getResultsReadyAndSuccessful() {
    return this.resultsState == SystemResultsState.Ready &&
      this.loadResults.getOutcome() == SystemResultsOutcome.Success;
  }

  getResultsProgressStr() {
    if (this.resultsState == SystemResultsState.Calculating) {
      return this.loadResults.getProgressStr();
    }
    return 'No calculation in progress';
  }

  getResultsDetailedProgressStr() {
    if (this.resultsState == SystemResultsState.Calculating) {
      return this.loadResults.getDetailedProgressStr();
    }
    return '';
  }

  getResultsProgressPercent() {
    if (this.resultsState == SystemResultsState.Calculating) {
      return this.loadResults.calcContext.getProgressPercent();
    }
    return 0;
  }

  getResultsTimeCalculating() {
    if (this.resultsState == SystemResultsState.Calculating) {
      return this.loadResults.getTimeCalculatingStr();
    }
    return '';
  }

  getSystemType() {
    return this.systemType.value;
  }

  getSystemName() {
    if (this.systemType.value == SystemType.CAV) {
      return 'CAV';
    } else if (this.systemType.value == SystemType.VAV) {
      return 'VAV';
    } else if (this.systemType.value == SystemType.RadiantInductionOrChilledBeam) {
      return 'Radiant Induction or Chilled Beam';
    } else {
      throw new Error(`Could not get name for system type: ${this.systemType.value}`);
    }
  }

  usingDemandControlledVentilation() {
    return this.demandControlledVentilation.get('use').value == YesNo.Yes;
  }

  getDemandControlledVentilationSchedule(occupancyScheduleData) {
    let matchOccupancySchedule = this.demandControlledVentilation.get('matchOccupancySchedule').value;
    if (matchOccupancySchedule == YesNo.Yes) {
      return occupancyScheduleData;
    } else {
      let schedule = this.demandControlledVentilation.get('schedule').lookupValue();
      return schedule.getData();
    }
  }

  getHeatingSupplyTemp() {
    if (this.systemType.value == SystemType.CAV) {
      return this.cavSystemInputs.heatingSupplyTemp.value;
    } else if (this.systemType.value == SystemType.VAV) {
      return this.vavSystemInputs.heatingSupplyTemp.value;
    } else {
      throw new Error(`Unsupported system type: ${this.systemType.value}`);
    }
  }

  getCoolingSupplyTemp() {
    if (this.systemType.value == SystemType.CAV) {
      return this.cavSystemInputs.coolingSupplyTemp.value;
    } else if (this.systemType.value == SystemType.VAV) {
      return this.vavSystemInputs.coolingSupplyTemp.value;
    } else {
      throw new Error(`Unsupported system type: ${this.systemType.value}`);
    }
  }
  
  isUsingEconomizer() {
    return this.economizer.get('use').value == YesNo.Yes;
  }

  usingSeparateAirflowsForHeatingCooling() {
    return this.separateAirflow.value == YesNo.Yes;
  }

  usesAutoVentilation() {
    return this.systemVentilationInputMode.value == VentilationInputMode.Ashrae62p1;
  }

  get_V_ot_manual() {
    return this.systemVentilationIntake.value;
  }

  get_V_ot_min_for_dcv() {
    return this.demandControlledVentilation.get('dcvMinVentilation').value;
  }

  getRecoveryType() {
    return this.heatRecovery.recoveryType.value;
  }

  createZone() {
    let zone = Zone.create(generateItemName('Zone', this.zones))
    zone.setupDefaults();
    this.zones.push(zone);
    return zone;
  }

  getZoneAtIndex(zoneIndex) {
    return this.zones[zoneIndex];
  }

  forEachZone(func) {
    for (let i = 0; i < this.zones.length; i++) {
      func({zoneIndex: i, zone: this.zones[i]});
    }
  }

  forEachSpaceInZone(zoneIndex, func) {
    let zone = this.zones[zoneIndex];
    for (let spaceIndex = 0; spaceIndex < zone.spaces.length; spaceIndex++) {
      let zoneSpace = zone.spaces[spaceIndex];
      func({
        zoneIndex,
        zone,
        spaceIndex,
        quantity: zoneSpace.quantity.value,
        spaceType: zoneSpace.getSpaceType()
      });
    }
  }

  forEachSpace(func) {
    for (let zoneIndex = 0; zoneIndex < this.zones.length; zoneIndex++) {
      let zone = this.zones[zoneIndex];
      for (let spaceIndex = 0; spaceIndex < zone.spaces.length; spaceIndex++) {
        let zoneSpace = zone.spaces[spaceIndex];
        func({
          zoneIndex,
          zone,
          spaceIndex,
          quantity: zoneSpace.quantity.value,
          spaceType: zoneSpace.getSpaceType()
        });
      }
    }
  }

  hasSingleSpace() {
    // Single space means: exactly 1 zone, that zone has exactly 1 space entry,
    // and that space entry has a quantity of 1.
    if (this.zones.length !== 1) return false;
    const zone = this.zones[0];
    if (zone.spaces.length !== 1) return false;
    return zone.spaces[0].quantity.value === 1;
  }

  isOccupied(hr) {
    // The system is occupied if any zone is occupied
    let occupied = false;
    this.forEachZone((zone) => {
      if (zone.zone.isOccupied(hr)) {
        occupied = true;
      }
    })
    return occupied;
  }

  getFracOccupied(hr) {
    /**
     * Estimate occupancy based on occupied spaces
     */
    let denominator = this.estimatedTotalOccupancy.value;
    if (denominator == 0) {
      // Use the peak coincident occupancy across all hours as the denominator
      for (let h = 0; h < 24; h++) {
        let coincident = 0;
        this.forEachSpace((space) => {
          coincident += space.spaceType.getNumOccupantsAtHour(h) * space.quantity;
        });
        if (coincident > denominator) {
          denominator = coincident;
        }
      }
    }
    if (denominator == 0) {
      return 0;
    }
    let totalOccupants = 0;
    this.forEachSpace((space) => {
      totalOccupants += space.spaceType.getNumOccupantsAtHour(hr) * space.quantity;
    });
    return Math.min(1.0, totalOccupants / denominator);
  }

  getTypesSet() {
    // All the types used by this system
    // This system + all Spaces + some schedules from inputs
    let typesSet = TypesSet.create();
    typesSet.addType(this, 'systems');
    this.forEachSpace((space) => {
      if (space.spaceType) {
        typesSet.addTypes(space.spaceType.getTypesSet());
      }
    })

    // Demand-controlled ventilation schedule, if relevant
    let matchOccupancySchedule = this.demandControlledVentilation.get('matchOccupancySchedule').value;
    if (matchOccupancySchedule == YesNo.No) {
      let schedule = this.demandControlledVentilation.get('schedule').lookupValue();
      typesSet.addType(schedule, 'schedules');
    }

    return typesSet;
  }

  _getProjectObjectsToIgnoreForErrors() {
    /*
    When we update the project errors before running calculations, we check all Project
    objects for errors. Really, we only have to check objects needed by this System. So
    make a list of objects to ignore.
    */
    let allTypes = gApp.proj().getTypesSet();
    let systemTypes = this.getTypesSet();
    allTypes.removeTypes(systemTypes);
    return allTypes.toList();
  }

  async calculateLoadsAsync() {
    if (this.resultsState == SystemResultsState.Calculating) {
      console.log("Already running calculations. Ignoring");
      return;
    }
    console.log("Calculating loads for system: ", this.name.value);
    this.resultsState = SystemResultsState.Calculating;

    // Check for project errors before running any calculations
    let objectsToIgnoreForErrors = this._getProjectObjectsToIgnoreForErrors();
    console.log("Using ignore list for errors: ", objectsToIgnoreForErrors);
    let projectErrorOpts = {
      ignoreSet: new Set(objectsToIgnoreForErrors),
    }
    let projectErrors = gApp.proj().updateProjectErrors(projectErrorOpts);
    if (!projectErrors.isEmpty()) {
      console.log("Project has errors, aborting calculation");
      this.loadResults = SystemLoadResults.create(null, null);
      this.loadResults.setProjectErrors(projectErrors);
      this.resultsState = SystemResultsState.Ready;
      return;
    }

    let ctx = CalcContext.create({
      recordLog: this.debugCalcOptions.recordCalculationLog
    });
    this.loadResults = SystemLoadResults.create(ctx, {
      projectName: gApp.proj().getName(),
      systemName: this.name.value,
    });
    this.loadResults.setProjectErrors(null);
    this.loadResults.setDesignTemps(
      this.designTempInputs.heatingDesignTemp.value,
      this.designTempInputs.coolingDesignTemp.value);
    try {
      ctx.debugOptions = this.debugCalcOptions;
      ctx.startDateStr = this.loadResults.getStartDateStr();
      let loadCalculator = new SystemLoadCalculator(this, gApp.proj(), ctx, this.debugCalcOptions);
      let results = await loadCalculator.calcLoads();
      this.loadResults.setResults(results);
    } catch (err) {
      console.log(`Error calculating loads for system ${this.name.value}:\n${err}\nTRACE:\n${err.stack}`);
      ctx.logFatalError(err);
      this.loadResults.setCalculationError(err);
      // Report the error. Errors here should ideally never happen if all pre-checks pass.
      gApp.reportError(err, {
        systemName: this.name.value,
        projName: gApp.proj().getName(),
      });
    } finally {
      this.resultsState = SystemResultsState.Ready;
    }
    //console.log("LOAD RESULTS: ", this.loadResults);
  }

  clearCalculationResults() {
    this.resultsState = SystemResultsState.None;
    this.loadResults = SystemLoadResults.create(null, null);
  }
}
setupClass(System)
